import { useState, type FormEvent } from 'react';
import { ArrowRight, CheckCircle2, LockKeyhole, Stethoscope } from 'lucide-react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Stack } from '../../shared/ui/Stack';
import { TextField } from '../../shared/ui/TextField';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../app/AuthProvider';
import type { UserRole } from '../../domain/types';
import { tokens } from '../../theme/tokens';

export function SignInPage() {
  const { user, signIn, demoMode, sessionError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(demoMode ? 'medica@consultorio.test' : '');
  const [password, setPassword] = useState(demoMode ? 'demonstracao' : '');
  const [role, setRole] = useState<UserRole>('medico');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await signIn(email, password, role);
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Não foi possível entrar. Confira seus dados ou tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function selectRole(nextRole: UserRole) {
    setRole(nextRole);
    setEmail(nextRole === 'medico' ? 'medica@consultorio.test' : 'secretaria@consultorio.test');
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px, 0.9fr) minmax(540px, 1.1fr)' },
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'relative',
          overflow: 'hidden',
          p: 12,
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: 'white',
          background: `linear-gradient(145deg, ${tokens.color.accentStrong} 0%, ${tokens.color.accentGradient} 58%, ${tokens.color.eucalyptusGradient} 130%)`,
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 520,
            height: 520,
            borderRadius: '50%',
            right: -250,
            bottom: -180,
            border: `1px solid ${tokens.overlay.whiteStrong}`,
            boxShadow: tokens.shadow.signInRings,
          },
        }}
      >
        <Stack direction="row" alignItems="center" gap={3}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 3,
              bgcolor: tokens.overlay.whiteMedium,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Stethoscope size={21} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 760, fontSize: '1.05rem' }}>Priscilla Falcão</Typography>
            <Typography sx={{ opacity: 0.72, fontSize: '0.8rem' }}>Psiquiatra</Typography>
          </Box>
        </Stack>
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 540 }}>
          <Typography
            sx={{
              fontSize: 'clamp(2.1rem, 4vw, 3.4rem)',
              lineHeight: 1.08,
              letterSpacing: '-0.045em',
              fontWeight: 700,
            }}
          >
            Cuidado atento também na rotina do consultório.
          </Typography>
          <Typography
            sx={{ mt: 5, maxWidth: 480, opacity: 0.74, fontSize: '1rem', lineHeight: 1.7 }}
          >
            Agenda, pacientes, tarefas e recebimentos em um ambiente sereno, organizado e protegido.
          </Typography>
          <Stack gap={2.5} sx={{ mt: 8 }}>
            {[
              'Visão clara das prioridades do dia',
              'Histórico operacional preservado',
              'Acesso definido por perfil profissional',
            ].map((item) => (
              <Stack key={item} direction="row" alignItems="center" gap={2.5}>
                <CheckCircle2 size={17} />
                <Typography sx={{ fontSize: '0.86rem', opacity: 0.9 }}>{item}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Typography sx={{ opacity: 0.55, fontSize: '0.75rem' }}>
          Ambiente restrito à equipe autorizada
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', placeItems: 'center', px: { xs: 4, sm: 10 }, py: 8 }}>
        <Paper
          component="form"
          onSubmit={(event) => void handleSubmit(event)}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 5, sm: 8 },
            borderRadius: 6,
            border: { xs: `1px solid ${tokens.color.border}`, lg: 0 },
            boxShadow: { xs: tokens.shadow.interactive, lg: 'none' },
          }}
        >
          <Box
            sx={{
              display: { lg: 'none' },
              width: 48,
              height: 48,
              borderRadius: 3.5,
              bgcolor: 'primary.main',
              color: 'white',
              placeItems: 'center',
              mb: 6,
            }}
          >
            <Stethoscope size={22} />
          </Box>
          <Typography component="h1" variant="h1">
            Bem-vinda de volta
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2, mb: 7 }}>
            Entre com sua conta do consultório para continuar.
          </Typography>

          {demoMode ? (
            <Box sx={{ mb: 5 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 2, fontWeight: 700 }}>
                Perfil para demonstração
              </Typography>
              <ToggleButtonGroup
                value={role}
                exclusive
                fullWidth
                onChange={(_, value: UserRole | null) => value && selectRole(value)}
                aria-label="Perfil para demonstração"
              >
                <ToggleButton value="medico">Médico</ToggleButton>
                <ToggleButton value="secretaria">Secretária</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : null}

          <Stack gap={4}>
            <TextField
              label="E-mail"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Senha"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
            />
          </Stack>
          {error || sessionError ? (
            <Alert severity="error" sx={{ mt: 4 }}>
              {error || sessionError}
            </Alert>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting}
            endIcon={
              submitting ? <CircularProgress color="inherit" size={18} /> : <ArrowRight size={18} />
            }
            sx={{ mt: 6 }}
          >
            {submitting ? 'Entrando…' : 'Entrar com segurança'}
          </Button>
          <Stack direction="row" gap={2} alignItems="center" justifyContent="center" sx={{ mt: 5 }}>
            <LockKeyhole size={14} color={tokens.color.textMuted} />
            <Typography variant="caption">
              Seus dados são protegidos e o acesso é registrado.
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
